import { Button, ConfigProvider, message, Modal, Progress, Result } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { ElectronicContractRepo } from "@/core/repos/tables/electronic-contract.repo";
import { CreateElectronicContractModel } from "@/core/models/electronic-contract.model";
import { DownloadOutlined } from "@ant-design/icons";
import jsonToCsvExport from "json-to-csv-export";

const electronicContractRepo = container.get(ElectronicContractRepo);

interface MakeMultiElectronicContractModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: ContractModel[],
}

interface FailedContract {
    id: string;
    waterUserCode: string;
    customerName: string;
    errorMessage: string;
}

const headers = [
    { key: "id", label: "ID" },
    { key: "waterUserCode", label: "Mã KH" },
    { key: "customerName", label: "Tên KH" },
    { key: "errorMessage", label: "Lỗi" },
];

export const MakeMultiElectronicContractModal: React.FC<MakeMultiElectronicContractModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [finished, setFinished] = useState<boolean>(false);
    const [processed, setProcessed] = useState<string[]>([]);
    const [failed, setFailed] = useState<FailedContract[]>([]);

    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        setProcessed([]);
        setFailed([]);
        setFinished(false);
    }
    
    useEffect(() => {
        handleDataChange();
    }, [data])

    const handleSave = async () => {
        try {
            setLoading(true);
            message.warning({
                content: "Đang tạo hợp đồng điện tử, vui lòng không đóng cửa sổ này..."
            });
            
            const defaultTemplateId = "DF02483B-9376-4C62-8202-0222EC7DA2FD";
            
            for (let i = 0; i < data.length; i++) {
                if (!isModalOpen) break;
                const contract = data[i];
                const payload: CreateElectronicContractModel = {
                    waterUserCode: contract.code,
                    templateId: defaultTemplateId
                };
                
                const result = await electronicContractRepo.createContract(payload);
                if (result?.code === "00" && result.result) {
                    setProcessed(prev => [...prev, result.result.id]);
                } else {
                    setFailed(prev => [...prev, {
                        id: contract.id,
                        waterUserCode: contract.code,
                        customerName: contract.name,
                        errorMessage: result?.errorMessage || "Tạo hợp đồng điện tử thất bại!"
                    }]);
                }
            }
            setFinished(true);
            electronicContractRepo.refresh();
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    
    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={800} 
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        {processed.length === 0 && failed.length === 0 && (
                            <Button 
                                key="submit" 
                                type="primary" 
                                loading={loading}
                                style={{ 
                                    background: "var(--primary-color)", 
                                    paddingRight: 48, 
                                    paddingLeft: 48, 
                                    fontSize: 18,
                                    fontWeight: 600,
                                    paddingTop: 18, 
                                    paddingBottom: 18,
                                }}
                                onClick={handleSave}
                            >
                                TẠO HỢP ĐỒNG
                            </Button>
                        )}
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">TẠO HỢP ĐỒNG ĐIỆN TỬ</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <div className="w-full">
                        {(processed.length === 0 && failed.length === 0) && (
                            <div>
                                <div className="text-lg pb-2">
                                    <p><b>Số lượng khách hàng được chọn: <span style={{color: "var(--primary-color)"}}>{data.length}</span></b></p>
                                </div>
                                <p className="text-gray-600 mt-4">Nhấn "TẠO HỢP ĐỒNG" để bắt đầu tạo hợp đồng điện tử cho các khách hàng đã chọn.</p>
                            </div>
                        )}
                        <div>
                            {(processed.length > 0 || failed.length > 0) && !finished && (
                                <div className="mb-4">
                                    <p>Đang tạo hợp đồng điện tử cho {data.length} khách hàng...</p>
                                    <Progress
                                        percent={Math.round(((processed.length + failed.length) / data.length) * 100)}
                                        status="normal"
                                        format={() => `${processed.length + failed.length} / ${data.length}`}
                                    />
                                </div>
                            )}
                            {finished && (
                                <Result
                                    status={processed.length > 0 ? "success" : "error"}
                                    title={`Đã tạo hợp đồng điện tử cho ${processed.length}/${data.length} khách hàng!`}
                                    subTitle="Quá trình tạo hợp đồng đã hoàn tất, bạn có thể đóng cửa sổ này."
                                />
                            )}
                            {failed.length > 0 && (
                                <div className="mt-4">
                                    <div className="flex flex-row items-center justify-between mb-2">
                                        <p className="text-red-600 font-bold mb-2">Tạo hợp đồng thất bại: ({failed.length})</p>
                                        <Button
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            danger
                                            onClick={() => jsonToCsvExport({
                                                data: failed, headers
                                            })}
                                        >
                                            Tải xuống file lỗi
                                        </Button>
                                    </div>
                                    <table className="w-full border border-red-400 text-sm">
                                        <thead>
                                            <tr className="bg-red-100">
                                                <th className="border border-red-300 px-2 py-1">Mã KH</th>
                                                <th className="border border-red-300 px-2 py-1">Tên KH</th>
                                                <th className="border border-red-300 px-2 py-1">Lỗi</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {failed.map((item, idx) => (
                                                <tr key={item.id} className={idx % 2 === 0 ? "bg-white" : "bg-red-50"}>
                                                    <td className="border border-red-200 px-2 py-1">{item.waterUserCode}</td>
                                                    <td className="border border-red-200 px-2 py-1">{item.customerName}</td>
                                                    <td className="border border-red-200 px-2 py-1 text-red-700">{item.errorMessage}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}





